import { supabase } from "./supabase";
import {
  AppData,
  Athlete,
  Attempt,
  Judge,
  Route,
  AthleteScore,
  RouteResult,
} from "@shared/types";
import { ROUTES } from "./store";

type RealtimeCallback = () => void;

class SupabaseDataStore {
  private realtimeCallbacks: Set<RealtimeCallback> = new Set();
  private realtimeSubscription: any = null;

  // Expose supabase client for direct access when needed
  public supabase = supabase;

  constructor() {
    this.setupRealtimeSubscription();
  }

  private setupRealtimeSubscription() {
    console.log("🔄 Setting up Supabase real-time subscription...");

    // Subscribe to all table changes with enhanced logging
    this.realtimeSubscription = supabase
      .channel("boulder-festival-changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "athletes" },
        (payload) => {
          console.log(
            "⚡ Real-time: Athletes table changed",
            payload.eventType,
          );
          this.notifyCallbacks();
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "attempts" },
        (payload) => {
          console.log(
            "⚡ Real-time: Attempts table changed",
            payload.eventType,
          );
          this.notifyCallbacks();
        },
      )
      .subscribe((status) => {
        console.log("📡 Real-time subscription status:", status);
      });
  }

  // Add callback for real-time updates
  onRealtimeUpdate(callback: RealtimeCallback) {
    this.realtimeCallbacks.add(callback);
    return () => this.realtimeCallbacks.delete(callback);
  }

  private notifyCallbacks() {
    console.log(
      `🔔 Notifying ${this.realtimeCallbacks.size} real-time callback(s) INSTANTLY`,
    );

    // Execute callbacks immediately for fastest response
    this.realtimeCallbacks.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        console.error("❌ Error in real-time callback:", error);
      }
    });
  }

  // Test basic internet connectivity
  async testNetworkConnectivity(): Promise<boolean> {
    try {
      console.log("🌐 Testing basic network connectivity...");

      // Try to fetch a simple endpoint
      const response = await fetch("https://httpbin.org/get", {
        method: "GET",
        cache: "no-cache",
        signal: AbortSignal.timeout(5000), // 5 second timeout
      });

      if (response.ok) {
        console.log("✅ Basic network connectivity working");
        return true;
      } else {
        console.error("❌ Network test failed with status:", response.status);
        return false;
      }
    } catch (err) {
      console.error("❌ Network connectivity test failed:", err);
      return false;
    }
  }

  // Basic connectivity test
  async testConnection(): Promise<boolean> {
    try {
      console.log("🔍 Testing basic Supabase connectivity...");

      // Skip general network test and go directly to Supabase test

      // Import the actual supabase instance to get the real URL and key being used
      const supabaseUrlFromEnv = import.meta.env.VITE_SUPABASE_URL;
      const supabaseKeyFromEnv = import.meta.env.VITE_SUPABASE_ANON_KEY;

      // Check if using environment variables or fallback values
      const usingEnvVars = !!(supabaseUrlFromEnv && supabaseKeyFromEnv);

      console.log(
        "🔍 Environment Variables:",
        usingEnvVars ? "SET" : "USING FALLBACK VALUES",
      );
      console.log(
        "🔍 Supabase URL:",
        usingEnvVars
          ? `${supabaseUrlFromEnv.substring(0, 30)}...`
          : "Using hardcoded fallback",
      );
      console.log(
        "🔍 Supabase Key:",
        usingEnvVars
          ? `${supabaseKeyFromEnv.substring(0, 20)}...`
          : "Using hardcoded fallback",
      );

      if (!usingEnvVars) {
        console.log(
          "ℹ️ No environment variables set, using hardcoded Supabase credentials (development mode)",
        );
      }

      // Test Supabase REST API accessibility
      try {
        const supabaseUrl =
          import.meta.env.VITE_SUPABASE_URL ||
          "https://xnwouwavvdfjewsbyvkb.supabase.co";
        const supabaseKey =
          import.meta.env.VITE_SUPABASE_ANON_KEY ||
          "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inhud291d2F2dmRmamV3c2J5dmtiIiwicm9sZSI6ImFub24iLCJpYXQiOjE3ODA0OTk0NzEsImV4cCI6MjA5NjA3NTQ3MX0.DSLQq85VMuCqvBx-oKyC41qtiI7zeZYVQ2kfHN-vfUc";

        console.log("🔗 Testing Supabase REST API accessibility...");

        // Test the actual REST API endpoint
        const urlResponse = await fetch(`${supabaseUrl}/rest/v1/`, {
          method: "GET",
          headers: {
            apikey: supabaseKey,
            Authorization: `Bearer ${supabaseKey}`,
          },
          cache: "no-cache",
          signal: AbortSignal.timeout(10000), // 10 second timeout
        });

        console.log("🏥 Supabase REST API status:", urlResponse.status);

        // Status 200 = OK, 404 = not found but reachable, 401/403 = auth issues but reachable
        const reachableStatuses = [200, 401, 403, 404, 405]; // 405 = Method not allowed, but reachable

        if (!reachableStatuses.includes(urlResponse.status)) {
          console.error(
            "⚠️ Supabase URL may be unreachable or returning unexpected status",
          );
          console.error(
            `Expected status codes: ${reachableStatuses.join(", ")}, Got: ${urlResponse.status}`,
          );
        } else {
          console.log("✅ Supabase URL is reachable");
        }
      } catch (urlErr) {
        console.error("❌ Supabase URL test failed:", urlErr);
        if (urlErr.name === "AbortError") {
          console.error(
            "⏰ Request timed out - Supabase may be slow or unreachable",
          );
        } else if (urlErr.message?.includes("Failed to fetch")) {
          console.error(
            "🌐 Network error - check internet connection or URL validity",
          );
          console.log(
            "ℹ️ This might be due to browser extensions or analytics interfering with fetch",
          );
        }

        // Don't fail completely if manual fetch fails - still try Supabase client
        console.log(
          "🔄 Manual fetch failed, but will continue with Supabase client test...",
        );
      }

      // Simple query to test connection
      const { data, error } = await supabase
        .from("judges")
        .select("count")
        .limit(1);

      if (error) {
        console.error("❌ Connection test failed:");
        console.error("Error type:", typeof error);
        console.error("Error object:", error);
        console.error("Error message:", error?.message);
        console.error("Error code:", error?.code);
        console.error("Error details:", error?.details);
        console.error("Error hint:", error?.hint);
        return false;
      }

      console.log("✅ Supabase connection test successful");
      console.log("Query result:", data);
      return true;
    } catch (err) {
      console.error("❌ Unexpected error in testConnection:", err);
      console.error("Error type:", typeof err);
      console.error("Error name:", err?.name);
      console.error("Error message:", err?.message);
      return false;
    }
  }

  // Diagnosis function for attempts table issues
  async diagnoseAttemptsTable(): Promise<void> {
    console.log("🔬 Running diagnosis on attempts table...");

    try {
      // Test if table exists and is accessible
      const { data: tableInfo, error: tableError } = await supabase
        .from("attempts")
        .select("*")
        .limit(0);

      if (tableError) {
        console.error("❌ Attempts table diagnosis failed:");
        console.error("Table error:", tableError);

        if (tableError.code === "PGRST116") {
          console.error(
            '🚨 Table "attempts" does not exist or is not accessible',
          );
          console.log(
            "💡 You may need to create the table using the SQL setup script",
          );
        } else if (tableError.code === "PGRST301") {
          console.error("🚨 Permission denied - check RLS policies");
          console.log("💡 You may need to set up Row Level Security policies");
        }
        return;
      }

      console.log("✅ Attempts table is accessible");

      // Test basic insert/select
      console.log("🧪 Testing basic operations...");

      // Get current count
      const { count, error: countError } = await supabase
        .from("attempts")
        .select("*", { count: "exact", head: true });

      if (countError) {
        console.error("❌ Count query failed:", countError);
        return;
      }

      console.log(`📊 Current attempts count: ${count || 0}`);
      console.log("✅ Attempts table diagnosis completed successfully");
    } catch (err) {
      console.error("❌ Diagnosis failed with unexpected error:", err);
    }
  }

  // Athletes
  async getAthletes(): Promise<Athlete[]> {
    try {
      const { data, error } = await supabase
        .from("athletes")
        .select("*")
        .order("name");

      if (error) {
        console.error("Error fetching athletes:", error);
        return [];
      }

      return data.map((athlete) => ({
        id: athlete.id,
        name: athlete.name,
        gender: athlete.gender,
        timeSlot: athlete.time_slot,
        createdAt: athlete.created_at,
      }));
    } catch (error) {
      console.error("❌ Unexpected error in getAthletes:", error);
      return [];
    }
  }

  async addAthlete(
    athlete: Omit<Athlete, "id" | "createdAt">,
  ): Promise<Athlete | null> {
    const { data, error } = await supabase
      .from("athletes")
      .insert({
        name: athlete.name,
        gender: athlete.gender,
        time_slot: athlete.timeSlot,
      })
      .select()
      .single();

    if (error) {
      console.error("Error adding athlete:", error);
      return null;
    }

    return {
      id: data.id,
      name: data.name,
      gender: data.gender,
      timeSlot: data.time_slot,
      createdAt: data.created_at,
    };
  }

  async updateAthlete(athlete: Athlete): Promise<boolean> {
    const { error } = await supabase
      .from("athletes")
      .update({
        name: athlete.name,
        gender: athlete.gender,
        time_slot: athlete.timeSlot,
      })
      .eq("id", athlete.id);

    if (error) {
      console.error("Error updating athlete:", error);
      return false;
    }

    return true;
  }

  async removeAthlete(athleteId: string): Promise<boolean> {
    const { error } = await supabase
      .from("athletes")
      .delete()
      .eq("id", athleteId);

    if (error) {
      console.error("Error removing athlete:", error);
      return false;
    }

    return true;
  }

  async removeDuplicateAthletes(): Promise<{ removed: number; kept: number }> {
    console.log("🔍 Checking for duplicate athletes...");

    const allAthletes = await this.getAthletes();
    const nameMap = new Map<string, Athlete[]>();

    // Group athletes by normalized name
    allAthletes.forEach((athlete) => {
      const normalizedName = athlete.name.toLowerCase().trim();
      if (!nameMap.has(normalizedName)) {
        nameMap.set(normalizedName, []);
      }
      nameMap.get(normalizedName)!.push(athlete);
    });

    let removedCount = 0;
    let keptCount = 0;

    // Remove duplicates (keep the first one, remove the rest)
    for (const [name, athletes] of nameMap) {
      if (athletes.length > 1) {
        console.log(`🔍 Found ${athletes.length} duplicates for "${name}"`);

        // Sort by creation date (keep the oldest one)
        athletes.sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );

        const toKeep = athletes[0];
        const toRemove = athletes.slice(1);

        console.log(`✅ Keeping: ${toKeep.name} (ID: ${toKeep.id})`);
        keptCount++;

        for (const athlete of toRemove) {
          console.log(`🗑️ Removing: ${athlete.name} (ID: ${athlete.id})`);
          const removed = await this.removeAthlete(athlete.id);
          if (removed) {
            removedCount++;
          }
        }
      } else {
        keptCount++;
      }
    }

    console.log(
      `✅ Duplicate cleanup completed: ${removedCount} removed, ${keptCount} kept`,
    );
    return { removed: removedCount, kept: keptCount };
  }

  // Attempts
  async getAttempts(): Promise<Attempt[]> {
    const { data, error } = await supabase
      .from("attempts")
      .select("*")
      .order("timestamp");

    if (error) {
      console.error("Error fetching attempts:", error);
      return [];
    }

    return data.map((attempt) => ({
      id: attempt.id,
      athleteId: attempt.athlete_id,
      routeNumber: attempt.route_number,
      timestamp: attempt.timestamp,
      result: attempt.result as "completed" | "failed",
      attemptNumber: attempt.attempt_number,
      points: attempt.points,
      pointType: attempt.point_type as "flash" | "second+" | "none",
    }));
  }

  async getAthleteAttempts(athleteId: string): Promise<Attempt[]> {
    const { data, error } = await supabase
      .from("attempts")
      .select("*")
      .eq("athlete_id", athleteId)
      .order("timestamp");

    if (error) {
      console.error("❌ Error fetching athlete attempts:");
      console.error("Error object:", error);
      console.error("Error message:", error?.message);
      console.error("Error code:", error?.code);
      console.error("Error details:", error?.details);
      console.error("Error hint:", error?.hint);
      console.error("Athlete ID:", athleteId);

      // Run table diagnosis if this is a table-related error
      if (error.code === "PGRST116" || error.code === "PGRST301") {
        await this.diagnoseAttemptsTable();
      }

      return [];
    }

    return data.map((attempt) => ({
      id: attempt.id,
      athleteId: attempt.athlete_id,
      routeNumber: attempt.route_number,
      timestamp: attempt.timestamp,
      result: attempt.result as "completed" | "failed",
      attemptNumber: attempt.attempt_number,
      points: attempt.points,
      pointType: attempt.point_type as "flash" | "second+" | "none",
    }));
  }

  async getRouteAttempts(
    athleteId: string,
    routeNumber: number,
  ): Promise<Attempt[]> {
    const { data, error } = await supabase
      .from("attempts")
      .select("*")
      .eq("athlete_id", athleteId)
      .eq("route_number", routeNumber)
      .order("timestamp");

    if (error) {
      console.error("❌ Error fetching route attempts:");
      console.error("Error object:", error);
      console.error("Error message:", error?.message);
      console.error("Error code:", error?.code);
      console.error("Error details:", error?.details);
      console.error("Error hint:", error?.hint);
      console.error("Athlete ID:", athleteId);
      console.error("Route number:", routeNumber);

      // Run table diagnosis if this is a table-related error
      if (error.code === "PGRST116" || error.code === "PGRST301") {
        await this.diagnoseAttemptsTable();
      }

      return [];
    }

    return data.map((attempt) => ({
      id: attempt.id,
      athleteId: attempt.athlete_id,
      routeNumber: attempt.route_number,
      timestamp: attempt.timestamp,
      result: attempt.result as "completed" | "failed",
      attemptNumber: attempt.attempt_number,
      points: attempt.points,
      pointType: attempt.point_type as "flash" | "second+" | "none",
    }));
  }

  async addAttempt(
    athleteId: string,
    routeNumber: number,
    completed: boolean,
  ): Promise<Attempt | null> {
    try {
      console.log(
        `🎯 Adding attempt: Athlete ${athleteId}, Route ${routeNumber}, Completed: ${completed}`,
      );

      // Get existing attempts for this athlete and route
      const existingAttempts = await this.getRouteAttempts(
        athleteId,
        routeNumber,
      );
      const attemptNumber = existingAttempts.length + 1;

      // Check if already completed
      const hasCompleted = existingAttempts.some(
        (a) => a.result === "completed",
      );
      if (hasCompleted && completed) {
        console.warn("⚠️ Athlete has already completed this route");
        return null;
      }

      // Calculate points
      let points = 0;
      let pointType: "flash" | "second+" | "none" = "none";

      if (completed && !hasCompleted) {
        const route = ROUTES.find((r) => r.number === routeNumber);
        if (route) {
          if (attemptNumber === 1) {
            points = route.flashPoints;
            pointType = "flash";
          } else {
            points = route.secondPlusPoints;
            pointType = "second+";
          }
        }
      }

      console.log(
        `📊 Calculated: attempt ${attemptNumber}, points ${points}, type ${pointType}`,
      );

      const { data, error } = await supabase
        .from("attempts")
        .insert({
          athlete_id: athleteId,
          route_number: routeNumber,
          result: completed ? "completed" : "failed",
          attempt_number: attemptNumber,
          points: points,
          point_type: pointType,
          timestamp: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) {
        console.error("❌ Error adding attempt:", error);
        return null;
      }

      console.log("✅ Attempt added successfully:", data);

      return {
        id: data.id,
        athleteId: data.athlete_id,
        routeNumber: data.route_number,
        timestamp: data.timestamp,
        result: data.result as "completed" | "failed",
        attemptNumber: data.attempt_number,
        points: data.points,
        pointType: data.point_type as "flash" | "second+" | "none",
      };
    } catch (error) {
      console.error("❌ Unexpected error in addAttempt:", error);
      return null;
    }
  }

  async removeLastAttempt(
    athleteId: string,
    routeNumber: number,
  ): Promise<boolean> {
    try {
      console.log(
        `🗑️ Removing last attempt for athlete ${athleteId}, route ${routeNumber}`,
      );

      // Get all attempts for this athlete and route, ordered by timestamp desc
      const { data: attempts, error: fetchError } = await supabase
        .from("attempts")
        .select("*")
        .eq("athlete_id", athleteId)
        .eq("route_number", routeNumber)
        .order("timestamp", { ascending: false })
        .limit(1);

      if (fetchError) {
        console.error("❌ Error fetching attempts for removal:", fetchError);
        return false;
      }

      if (!attempts || attempts.length === 0) {
        console.log("ℹ️ No attempts found to remove");
        return false;
      }

      const lastAttempt = attempts[0];
      console.log(`🎯 Removing attempt: ${lastAttempt.id}`);

      const { error: deleteError } = await supabase
        .from("attempts")
        .delete()
        .eq("id", lastAttempt.id);

      if (deleteError) {
        console.error("❌ Error deleting attempt:", deleteError);
        return false;
      }

      console.log("✅ Last attempt removed successfully");
      return true;
    } catch (error) {
      console.error("❌ Unexpected error in removeLastAttempt:", error);
      return false;
    }
  }

  async removeAllRouteAttempts(
    athleteId: string,
    routeNumber: number,
  ): Promise<number> {
    try {
      console.log(
        `🗑️ Removing all attempts for athlete ${athleteId}, route ${routeNumber}`,
      );

      // Get count first
      const { data: attempts, error: fetchError } = await supabase
        .from("attempts")
        .select("id")
        .eq("athlete_id", athleteId)
        .eq("route_number", routeNumber);

      if (fetchError) {
        console.error("❌ Error fetching attempts for counting:", fetchError);
        return 0;
      }

      const attemptCount = attempts?.length || 0;
      if (attemptCount === 0) {
        console.log("ℹ️ No attempts found to remove");
        return 0;
      }

      // Delete all attempts for this athlete and route
      const { error: deleteError } = await supabase
        .from("attempts")
        .delete()
        .eq("athlete_id", athleteId)
        .eq("route_number", routeNumber);

      if (deleteError) {
        console.error("❌ Error deleting route attempts:", deleteError);
        return 0;
      }

      console.log(`✅ ${attemptCount} attempts removed successfully`);
      return attemptCount;
    } catch (error) {
      console.error("❌ Unexpected error in removeAllRouteAttempts:", error);
      return 0;
    }
  }

  async clearAllAttempts(): Promise<boolean> {
    try {
      console.log("🗑️ Clearing ALL attempts from database...");

      const { error } = await supabase
        .from("attempts")
        .delete()
        .neq("id", "00000000-0000-0000-0000-000000000000"); // Delete all records

      if (error) {
        console.error("❌ Error clearing attempts:", error);
        return false;
      }

      console.log("✅ All attempts cleared successfully");
      return true;
    } catch (error) {
      console.error("❌ Unexpected error in clearAllAttempts:", error);
      return false;
    }
  }

  // Judges
  async getJudges(): Promise<Judge[]> {
    const { data, error } = await supabase.from("judges").select("*");

    if (error) {
      console.error("Error fetching judges:", error);
      return [];
    }

    return data.map((judge) => ({
      id: judge.id,
      name: judge.name,
      password: judge.password,
    }));
  }

  async validateJudge(password: string): Promise<Judge | null> {
    const { data, error } = await supabase
      .from("judges")
      .select("*")
      .eq("password", password)
      .single();

    if (error || !data) {
      return null;
    }

    return {
      id: data.id,
      name: data.name,
      password: data.password,
    };
  }

  // Scoring
  async getAthleteScore(athleteId: string): Promise<AthleteScore> {
    const attempts = await this.getAthleteAttempts(athleteId);

    // Group by route and get best score for each route
    const routeScores = new Map<
      number,
      { points: number; routeNumber: number }
    >();

    attempts.forEach((attempt) => {
      if (attempt.result === "completed") {
        const existing = routeScores.get(attempt.routeNumber);
        if (!existing || attempt.points > existing.points) {
          routeScores.set(attempt.routeNumber, {
            points: attempt.points,
            routeNumber: attempt.routeNumber,
          });
        }
      }
    });

    // Get top 6 routes
    const allScores = Array.from(routeScores.values())
      .sort((a, b) => b.points - a.points)
      .slice(0, 6);

    const totalScore = allScores.reduce((sum, score) => sum + score.points, 0);

    return {
      totalScore,
      topSixRoutes: allScores,
    };
  }

  async getRanking(
    gender: "Masculino" | "Feminino",
  ): Promise<{ athlete: Athlete; score: AthleteScore }[]> {
    console.log(`🏆 Getting optimized ranking for ${gender}...`);
    const startTime = performance.now();

    try {
      // Get athletes and their attempts in a single optimized query
      const { data: athletesWithAttempts, error } = await supabase
        .from("athletes")
        .select(
          `
          *,
          attempts:attempts(*)
        `,
        )
        .eq("gender", gender)
        .order("name");

      if (error) {
        console.error("Error fetching ranking data:", error);
        throw error;
      }

      console.log(
        `📊 Fetched ${athletesWithAttempts?.length || 0} athletes with attempts in ${(performance.now() - startTime).toFixed(2)}ms`,
      );

      // Calculate scores locally for better performance
      const ranking =
        athletesWithAttempts?.map((athleteData) => {
          const athlete: Athlete = {
            id: athleteData.id,
            name: athleteData.name,
            gender: athleteData.gender,
            timeSlot: athleteData.time_slot,
            createdAt: athleteData.created_at,
          };

          // Calculate score from attempts
          const attempts = athleteData.attempts || [];
          const routeScores = new Map<
            number,
            { points: number; routeNumber: number }
          >();

          attempts.forEach((attempt: any) => {
            if (attempt.result === "completed") {
              const existing = routeScores.get(attempt.route_number);
              if (!existing || attempt.points > existing.points) {
                routeScores.set(attempt.route_number, {
                  points: attempt.points,
                  routeNumber: attempt.route_number,
                });
              }
            }
          });

          // Get top 6 routes
          const allScores = Array.from(routeScores.values())
            .sort((a, b) => b.points - a.points)
            .slice(0, 6);

          const totalScore = allScores.reduce(
            (sum, score) => sum + score.points,
            0,
          );

          const score: AthleteScore = {
            totalScore,
            topSixRoutes: allScores,
          };

          return { athlete, score };
        }) || [];

      const finalRanking = ranking.sort(
        (a, b) => b.score.totalScore - a.score.totalScore,
      );

      const totalTime = (performance.now() - startTime).toFixed(2);
      console.log(
        `🏆 Optimized ranking calculated in ${totalTime}ms (${finalRanking.length} athletes)`,
      );

      return finalRanking;
    } catch (error) {
      console.error(`❌ Error in optimized getRanking for ${gender}:`, error);

      // Fallback to the old method if the optimized version fails
      console.log("🔄 Falling back to individual queries...");
      const athletes = await this.getAthletes();
      const filteredAthletes = athletes.filter((a) => a.gender === gender);

      const ranking = await Promise.all(
        filteredAthletes.map(async (athlete) => ({
          athlete,
          score: await this.getAthleteScore(athlete.id),
        })),
      );

      return ranking.sort((a, b) => b.score.totalScore - a.score.totalScore);
    }
  }

  // Migration from localStorage
  async migrateFromLocalStorage(): Promise<void> {
    const localData = localStorage.getItem("boulder-festival-data");
    if (!localData) return;

    try {
      const data: AppData = JSON.parse(localData);

      // Migrate athletes
      if (data.athletes && data.athletes.length > 0) {
        const existingAthletes = await this.getAthletes();
        if (existingAthletes.length === 0) {
          console.log("Migrating athletes...");
          await this.importAthletes(data.athletes);
        }
      }

      // Note: Attempts will need manual migration as they have different structure
      console.log("Migration completed. You may want to clear localStorage.");
    } catch (error) {
      console.error("Migration error:", error);
    }
  }

  async importAthletes(
    athletes: Athlete[],
  ): Promise<{ imported: number; skipped: number }> {
    let imported = 0;
    let skipped = 0;

    for (const athlete of athletes) {
      try {
        const added = await this.addAthlete({
          name: athlete.name,
          gender: athlete.gender,
          timeSlot: athlete.timeSlot,
        });

        if (added) {
          imported++;
          console.log(`✅ Imported: ${athlete.name}`);
        } else {
          skipped++;
          console.log(`⚠️ Skipped: ${athlete.name} (error adding)`);
        }
      } catch (error) {
        skipped++;
        console.error(`❌ Error importing ${athlete.name}:`, error);
      }
    }

    console.log(`📊 Import complete: ${imported} imported, ${skipped} skipped`);
    return { imported, skipped };
  }

  destroy() {
    if (this.realtimeSubscription) {
      this.realtimeSubscription.unsubscribe();
    }
    this.realtimeCallbacks.clear();
  }
}

export const supabaseStore = new SupabaseDataStore();
